import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import { useToast } from "../toast";

interface Props {
  open: boolean;
  entityType: "driver" | "charger";
  entityId: number;
  onClose: () => void;
  onSaved: () => void;
}

export default function LabelDialog({ open, entityType, entityId, onClose, onSaved }: Props) {
  const api = useAuthApi();
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const basePath =
    entityType === "driver"
      ? `${MGMT}/drivers/${entityId}/labels`
      : `${MGMT}/charger-configs/${entityId}/labels`;

  const handleSave = async () => {
    if (!key.trim() || !value.trim()) return;
    setSaving(true);
    try {
      await api(basePath, {
        method: "POST",
        body: JSON.stringify({ key: key.trim(), value: value.trim() }),
      });
      toast.show("Label added");
      setKey("");
      setValue("");
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    setKey("");
    setValue("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>Add Label</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            required
            fullWidth
            placeholder="e.g. fleet, cost-center"
          />
          <TextField
            label="Value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required
            fullWidth
            placeholder="e.g. alpha, 2024-A"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !key.trim() || !value.trim()}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
